/**
 * ShareWithFriendsUseCase
 *
 * Fan-out share: write one chat message per selected peer in a single batched
 * Firestore commit. Mirrors `lib/presentation/bottom_sheet/search_users_bottom_sheet.dart`
 * `shareLink()` (lines 321-408) and `lib/data/repository/share_repository.dart`.
 *
 * Peer-id direction handling: each `ExclusiveContentSubscriptionEntity` carries
 * BOTH endpoints (`userId` = creator, `subscriberUserId` = subscriber) and a
 * direction flag `meSubscriber` indicating which side the current user occupies.
 * The peer is therefore the OTHER endpoint:
 *   - `meSubscriber === true`  → current user is the subscriber, peer is the
 *     creator at `subscription.userId`.
 *   - `meSubscriber === false` → current user is the creator, peer is the
 *     subscriber at `subscription.subscriberUserId`.
 * Treating `subscription.userId` as the peer unconditionally mis-addresses the
 * fan-out for subscriptions in the inverse direction (their messages would land
 * on the current user's own chat thread or on the wrong peer entirely). Flutter
 * surfaces the same `userId` vs `subscriberUserId` distinction at
 * `search_users_bottom_sheet.dart:809` ("Subscriber" vs "Subscribed" label).
 *
 * Encryption parity with the single-send path: when the share carries a text
 * body, the body is encrypted per peer before it lands in Firestore. Both
 * clients do this now — web here, and Flutter in
 * `ShareRepository.saveBatchChatMessages`
 * (`lib/data/repository/share_repository.dart`), which mirrors
 * `ChatRepository.saveChatMessage` (`chat_repository.dart:14-28`).
 * Verified Flutter bug, since fixed in both clients: Flutter's batch path used
 * to hand the caption straight to `ChatDataSource` with no `E2EE.encrypt` hop,
 * so the mobile client could not decrypt its own batched shares. That gap was
 * closed on the Flutter side in this same branch, so the two clients agree —
 * do not "restore parity" by dropping either side's encrypt.
 *
 * The encryption hop matches `SendTextChatMessageUseCase` / `ChatRepository.saveChatMessage`
 * exactly: only run when `content !== '' && content !== undefined`
 * (parity with `chat_repository.dart:17` — `content != null && content!.isNotEmpty`).
 * Empty/undefined message text passes through unencrypted (it is empty by
 * definition; nothing to protect), and `last_content` mirrors the same empty
 * value on the metadata node.
 *
 * Per-peer flow (runs SEQUENTIALLY inside the batch loop — see below):
 *   1. Resolve the peer's `UserEntity` via `GetUserUseCase` to read `publicJwk`.
 *   2. Initialize the encryption singleton with that peer's `publicJwk` via
 *      `InitializeTextEncryptionUseCase` (null when the peer has no key — the
 *      service falls through to pass-through mode, matching Flutter
 *      `e2ee_impl.dart:47-50`).
 *   3. Encrypt the plaintext text body via `EncryptTextUseCase`.
 *   4. Substitute the ciphertext into both `messageData.content` and
 *      `metadataData.last_content` before the batch commit.
 *
 * Sequential per-peer iteration is REQUIRED: `textEncryptionService` is a
 * singleton with a single `aesGcmKey` slot. If two peers' `initialize → encrypt`
 * pairs interleaved concurrently (e.g., under `Promise.all`), the second
 * `initialize` would overwrite the first peer's key before the first
 * `encrypt` ran, and both batch entries would land encrypted under the second
 * peer's key. Peer A would then be unable to decrypt their own message.
 * Therefore peer A's `encrypt` MUST complete before peer B's `initialize`
 * begins. The use cases this code calls (`InitializeTextEncryptionUseCase` /
 * `EncryptTextUseCase`) are thin wrappers around the singleton — they
 * inherit the same serialization requirement. A share running concurrently
 * with an open chat will still clobber the chat's derived key (pre-existing
 * cross-feature risk; out of scope here).
 */

import { ChatService } from '@/domain/services/chat/chatService';
import { ChatMessageRemapper } from '@/domain/remappers/chat/chatMessageRemapper';
import { ChatMessageNodeRemapper } from '@/domain/remappers/chat/chatMessageNodeRemapper';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import { getUserUseCase } from '@/domain/usecases/user/getUserUseCase';
import { initializeTextEncryptionUseCase } from '@/domain/usecases/textEncryption/initializeTextEncryptionUseCase';
import { encryptTextUseCase } from '@/domain/usecases/textEncryption/encryptTextUseCase';
import type { ShareInfoEntity } from '@/domain/entities/info/shareInfoEntity';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';

export class ShareWithFriendsUseCase {
  private chatService: ChatService;

  constructor() {
    this.chatService = ChatService.getInstance();
  }

  /**
   * Execute share operation
   *
   * @param shareInfo - Share context (user/photo/video being shared)
   * @param selectedSubscriptions - Users to share with
   * @param messageText - Optional message to include
   * @param currentUserId - Current user's Firebase ID
   * @param currentUserName - Current user's display name
   * @returns Promise<boolean> - true if successful, false otherwise
   */
  async execute(
    shareInfo: ShareInfoEntity,
    selectedSubscriptions: ExclusiveContentSubscriptionEntity[],
    messageText: string,
    currentUserId: string,
    currentUserName: string
  ): Promise<boolean> {
    const timestamp = Date.now();
    const messageType = ChatUtils.getShareMessageType(shareInfo.mediaInfo);

    // Build one batch entry per selected peer. Each entry encrypts the text
    // body (when non-empty) under the peer's own public JWK — see class JSDoc
    // for parity rationale.
    //
    // The loop is SEQUENTIAL on purpose: `textEncryptionService` is a singleton
    // with a single `aesGcmKey` slot. Concurrent peers (e.g., via `Promise.all`)
    // would race `initialize` and overwrite each other's derived keys before
    // `encrypt` ran. See class JSDoc for the full rationale.
    const batchMessages: Array<{
      groupChatId: string;
      messageId: string;
      messageData: ReturnType<typeof ChatMessageRemapper.toJson>;
      metadataData: ReturnType<typeof ChatMessageNodeRemapper.toJson>;
    }> = [];

    for (const subscription of selectedSubscriptions) {
      // Resolve the peer id from the subscription direction (see class JSDoc).
      // `meSubscriber` tells which endpoint the current user occupies; the peer
      // is always the OTHER endpoint. `subscription.userId` is NOT a peer id
      // on its own — for inverse-direction rows it points at the current user.
      const peerUserId = subscription.meSubscriber
        ? subscription.userId
        : subscription.subscriberUserId;

      const documentId = generateUuidV1();
      const groupChatId = ChatUtils.getGroupChatId(
        currentUserId,
        peerUserId
      );

      // Empty/undefined body short-circuits the encryption hop, mirroring
      // `chat_repository.dart:17` (`content != null && content!.isNotEmpty`).
      // The empty string still propagates through to Firestore as the
      // current `content` / `last_content`, matching the pre-fix shape.
      let encryptedText = messageText;
      if (messageText !== '' && messageText !== undefined) {
        const peer = await getUserUseCase.execute(peerUserId);
        await initializeTextEncryptionUseCase.execute(peer?.publicJwk ?? null);
        encryptedText = await encryptTextUseCase.execute(messageText);
      }

      const chatMessage: ChatMessageEntity = {
        documentId,
        chatId: groupChatId,
        idFrom: currentUserId,
        idTo: peerUserId,
        timestamp,
        content: encryptedText,
        type: messageType,
        success: true,
        seenByPeer: false,
        giftInstances: 1,
        shareInfo,
        users: [currentUserId, peerUserId],
      };

      // Remap message and message node to Firestore format
      const messageData = ChatMessageRemapper.toJson(ChatMessageRemapper.toDto(chatMessage));

      const chatMessageNode: ChatMessageNodeEntity = {
        chatId: groupChatId,
        idFrom: currentUserId,
        idTo: peerUserId,
        timestamp,
        createdTimestamp: timestamp,
        lastContent: encryptedText,
        type: messageType,
        nameUser1: currentUserName,
        nameUser2: subscription.name,
        seenByPeer: false,
        blocked: false,
        users: [currentUserId, peerUserId],
      };
      const metadataData = ChatMessageNodeRemapper.toJson(ChatMessageNodeRemapper.toDto(chatMessageNode));

      batchMessages.push({
        groupChatId,
        messageId: documentId,
        messageData,
        metadataData,
      });
    }

    // Send all messages in a single batch
    return this.chatService.saveBatchChatMessages(batchMessages);
  }
}

export const createShareWithFriendsUseCase = () => new ShareWithFriendsUseCase();
